import { useCallback, useEffect, useState } from "react";
import {
  api,
  ApiError,
  type AcaoDocumento,
  type Decisor,
  type EventoDocumento,
  type FluxoDocumento,
  type OrigemDecisor,
  type RevisaoDocumento,
} from "../api";
import { IconCheck, IconDownload, IconUpload } from "../icons";

/** Arquivo e textos escolhidos no pop-up — sobem junto com o "Salvar". */
export interface AnexoPendente {
  file: File | null;
  versao: string;
  descricao: string;
  /** A pessoa mexeu em algo daqui (sem isso o Salvar não manda nada do arquivo). */
  alterado: boolean;
}

export const ANEXO_VAZIO: AnexoPendente = { file: null, versao: "", descricao: "", alterado: false };

/** Mesma lista e limite do servidor (server/arquivos.ts) — lá é que vale. */
const ACEITOS = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".png", ".jpg", ".jpeg", ".txt", ".zip"];
const MAX_MB = 25;

/**
 * Os passos que a pessoa vê, na ordem do fluxo. Desde 22/10/2026 a revisão é
 * de quem elabora (passo 1), e não mais uma etapa com outra pessoa.
 */
const PASSOS = ["Elaboração e revisão", "Aprovação", "Publicação", "Vigente"];
/** Índice do último passo (o documento já vale). */
const PASSO_VIGENTE = PASSOS.length - 1;

function passoDa(etapa: string | null | undefined, temVigente: boolean): number {
  if (etapa === "Aguardando aprovação") return 1;
  if (etapa === "Aguardando publicação") return 2;
  if (!etapa && temVigente) return PASSO_VIGENTE;
  return 0;
}

/** Ainda com quem elabora: dá para trocar o arquivo e enviar. */
const comQuemElabora = (r: RevisaoDocumento | null) => !r || r.etapa === "Rascunho" || r.etapa === "Devolvido";

/** "05/10/2026 14:03", no horário de quem está vendo. */
function dataHora(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function dataCurta(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString("pt-BR");
}

function tamanho(bytes: number): string {
  return bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** "Fulano" · "Fulano e Beltrano" · "Fulano, Beltrano e Ciclano". */
function nomes(lista: { nome: string }[]): string {
  const n = lista.map((g) => g.nome);
  return n.length <= 1 ? n.join("") : `${n.slice(0, -1).join(", ")} e ${n[n.length - 1]}`;
}

/** "de Fulano" · "do administrador (ninguém configurado para este setor)". */
function deQuem(lista: Decisor[], origem: OrigemDecisor, etapa: "aprovar" | "publicar"): string {
  if (origem === "admin") return `do administrador (ninguém foi configurado para ${etapa} este setor)`;
  return `de ${nomes(lista)}`;
}

/** Como cada passo do histórico aparece. */
function textoDoEvento(e: EventoDocumento): string {
  switch (e.acao) {
    case "anexou":
      return `anexou ${e.observacao ? `o arquivo ${e.observacao}` : "o arquivo"}`;
    case "enviou":
      // Antes de 22/10/2026 o envio ia para a revisão de outra pessoa.
      return e.etapa === "Aguardando revisão" ? "enviou para revisão" : "elaborou, revisou e enviou para a aprovação";
    case "revisao_na_elaboracao":
      return "— a revisão passou a fazer parte da elaboração; o documento seguiu para a aprovação";
    case "trocou_aprovador":
      return e.observacao?.startsWith("De ") ? "trocou quem aprova" : "definiu quem aprova";
    case "revisou":
      return "revisou e mandou para aprovação";
    case "revisou_no_lugar":
      return "revisou no lugar de quem revisa o setor e mandou para aprovação";
    // Antes da etapa de publicação (13/10/2026), aprovar já tornava vigente.
    case "aprovou":
      return "aprovou — passou a ser a versão vigente";
    case "aprovou_no_lugar":
      return "aprovou no lugar de quem aprova o setor — passou a ser a versão vigente";
    case "aprovou_para_publicar":
      return "aprovou e mandou para a publicação";
    case "aprovou_para_publicar_no_lugar":
      return "aprovou no lugar de quem aprova o setor e mandou para a publicação";
    case "publicou":
      return "publicou — passou a ser a versão vigente";
    case "publicou_no_lugar":
      return "publicou no lugar de quem publica o setor — passou a ser a versão vigente";
    case "devolveu":
      return e.etapa === "Aguardando publicação"
        ? "devolveu na publicação"
        : e.etapa === "Aguardando aprovação"
          ? "devolveu na aprovação"
          : "devolveu na revisão";
    case "descartou":
      return "descartou o rascunho";
    case "ficou_obsoleta":
      return "uma revisão nova passou a valer — esta ficou obsoleta";
    case "obsoleto":
      return "tornou o documento obsoleto";
    default:
      return e.acao;
  }
}

/** A mesma pessoa elaborou e revisou (o passo 1 desde 22/10/2026). */
const elaborouERevisou = (r: RevisaoDocumento) =>
  !!r.elaborado_nome && r.revisado_nome === r.elaborado_nome && !r.revisado_substituto;

/** Quem assinou o quê numa revisão, em uma linha. */
function assinaturas(r: RevisaoDocumento): string {
  const partes: string[] = [];
  if (elaborouERevisou(r)) {
    partes.push(`elaborada e revisada por ${r.elaborado_nome}`);
  } else {
    if (r.elaborado_nome) partes.push(`elaborada por ${r.elaborado_nome}`);
    if (r.revisado_nome) {
      partes.push(`revisada por ${r.revisado_nome}${r.revisado_substituto ? " (no lugar de quem revisa)" : ""}`);
    }
  }
  if (r.aprovado_nome) {
    partes.push(
      `aprovada por ${r.aprovado_nome}${r.aprovado_substituto ? " (no lugar de quem aprova)" : ""} em ${dataCurta(r.aprovado_em)}`,
    );
  }
  if (r.publicado_nome) {
    partes.push(
      `publicada por ${r.publicado_nome}${r.publicado_substituto ? " (no lugar de quem publica)" : ""} em ${dataCurta(r.publicado_em)}`,
    );
  }
  if (partes.length === 0) {
    return r.descricao === "Anexado antes do fluxo de aprovação" ? "anexada antes do fluxo de aprovação" : "";
  }
  return partes.join(" · ");
}

interface Props {
  /** null = documento novo, ainda não salvo. */
  documentoId: number | null;
  anexo: AnexoPendente;
  onAnexo: (a: AnexoPendente) => void;
  /** Diz ao pop-up se dá para "Salvar e enviar para aprovação". */
  onPodeEnviar: (pode: boolean) => void;
  /** Uma decisão foi registrada (aprovação, publicação...): a lista recarrega. */
  onMudou: () => void;
  /** Muda quando o pop-up acabou de subir o arquivo: recarrega o fluxo. */
  recarga: number;
}

/**
 * Bloco "Arquivo e aprovação" do pop-up do documento.
 *
 * O campo do arquivo sobe junto com o Salvar do pop-up (é um campo do
 * cadastro, como "Quem aprova"). Já as decisões — aprovar, publicar, devolver,
 * descartar, tornar obsoleto — valem na hora, cada uma com o seu botão, porque
 * não dependem do resto do formulário. Quem decide o que cada pessoa pode fazer é o servidor
 * (`pode`): aqui só aparecem os botões que ele vai aceitar.
 */
export default function DocumentoAprovacao({
  documentoId,
  anexo,
  onAnexo,
  onPodeEnviar,
  onMudou,
  recarga,
}: Props) {
  const [fluxo, setFluxo] = useState<FluxoDocumento | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [parecer, setParecer] = useState("");
  const [obsoletando, setObsoletando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [arrastando, setArrastando] = useState(false);

  const carregar = useCallback(async () => {
    if (!documentoId) {
      setFluxo(null);
      return;
    }
    setCarregando(true);
    try {
      setFluxo(await api.documentos.fluxo(documentoId));
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível carregar a aprovação deste documento.");
    } finally {
      setCarregando(false);
    }
  }, [documentoId]);

  useEffect(() => {
    void carregar();
  }, [carregar, recarga]);

  const andamento = fluxo?.emAndamento ?? null;
  const vigente = fluxo?.vigente ?? null;
  const pode = fluxo?.pode ?? null;
  // Documento salvo cujo fluxo ainda não chegou: não oferece nada até saber como está.
  const conhecido = documentoId === null || fluxo !== null;
  const podeAnexar = conhecido && (documentoId === null || !!pode?.anexar);
  const podeEnviar =
    conhecido && comQuemElabora(andamento) && (!!anexo.file || !!andamento?.tem_arquivo) && podeAnexar;

  useEffect(() => {
    onPodeEnviar(podeEnviar);
  }, [podeEnviar, onPodeEnviar]);

  // Preenche revisão e "o que mudou" com o que já está no rascunho (ou a
  // sugestão da próxima revisão), enquanto a pessoa não mexeu em nada.
  useEffect(() => {
    if (anexo.alterado || !comQuemElabora(andamento)) return;
    onAnexo({
      file: null,
      versao: andamento?.versao ?? fluxo?.proximaVersao ?? "Rev. 00",
      descricao: andamento?.descricao ?? "",
      alterado: false,
    });
    // Só quando o fluxo (re)carrega — não a cada tecla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fluxo]);

  function escolher(file: File | null | undefined) {
    if (!file) return;
    const ext = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;
    if (!ACEITOS.includes(ext)) {
      setErro(`Tipo de arquivo não aceito (${ext}). Use PDF, Word, Excel, imagem, TXT ou ZIP.`);
      return;
    }
    if (file.size > MAX_MB * 1048576) {
      setErro(`Arquivo muito grande (${tamanho(file.size)}). O máximo é ${MAX_MB} MB.`);
      return;
    }
    setErro(null);
    onAnexo({ ...anexo, file, alterado: true });
  }

  async function agir(acao: AcaoDocumento, texto: string, confirmar?: string) {
    if (!documentoId || !fluxo) return;
    if ((acao === "devolver" || acao === "obsoleto") && !texto.trim()) {
      setErro(acao === "devolver" ? "Escreva no parecer o motivo da devolução." : "Escreva o motivo.");
      return;
    }
    if (acao === "aprovar" && pode?.aprovarNoLugar && !texto.trim()) {
      setErro("Você está aprovando no lugar de quem aprova este documento: escreva o motivo no parecer.");
      return;
    }
    if (acao === "publicar" && pode?.publicarNoLugar && !texto.trim()) {
      setErro("Você está publicando no lugar de quem publica este setor: escreva o motivo no parecer.");
      return;
    }
    if (confirmar && !confirm(confirmar)) return;
    setOcupado(acao);
    setErro(null);
    setAviso(null);
    try {
      const r = await api.documentos.acao(documentoId, acao, texto.trim(), andamento?.id ?? null);
      setParecer("");
      setMotivo("");
      setObsoletando(false);
      setAviso(r.mensagem);
      await carregar();
      onMudou();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível registrar.");
    } finally {
      setOcupado(null);
    }
  }

  async function baixar(r: RevisaoDocumento) {
    if (!documentoId) return;
    setOcupado(`baixar-${r.id}`);
    setErro(null);
    try {
      await api.documentos.baixarRevisao(documentoId, r.id, r.arquivo_nome);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível baixar o arquivo.");
    } finally {
      setOcupado(null);
    }
  }

  const passo = passoDa(andamento?.etapa, !!vigente);
  const obsoleto = fluxo?.documento.status === "Obsoleto";
  const devolucao =
    andamento?.etapa === "Devolvido"
      ? fluxo?.eventos.find((e) => e.acao === "devolveu" && e.revisao_id === andamento.id) ?? null
      : null;
  const quemAprova = fluxo ? deQuem(fluxo.aprovadores, fluxo.origemAprovacao, "aprovar") : "de quem aprova";
  const quemPublica = fluxo
    ? deQuem(fluxo.publicadores, fluxo.origemPublicacao, "publicar")
    : "de quem publica (Qualidade)";
  /** Decidindo no lugar de quem foi escolhido ou configurado: o parecer vira obrigatório. */
  const noLugar = !!(pode?.aprovarNoLugar || pode?.publicarNoLugar);
  /** A decisão que esta pessoa tem agora (no máximo uma etapa por vez). */
  const decisao: "aprovar" | "publicar" | null = pode?.publicar ? "publicar" : pode?.aprovar ? "aprovar" : null;
  const validade = fluxo?.validade ?? null;
  const linhaValidade = validade && (
    <small className="doc-validade-linha">
      Próxima revisão periódica: {dataCurta(`${validade.proxima}T12:00:00`)}
      {validade.situacao === "vencida" && <span className="pill pill-danger">Vencida</span>}
      {validade.situacao === "vencendo" && (
        <span className="pill pill-warn">
          {validade.dias === 0 ? "Vence hoje" : `Em ${validade.dias} dia${validade.dias === 1 ? "" : "s"}`}
        </span>
      )}
    </small>
  );

  const botaoBaixar = (r: RevisaoDocumento, rotulo: string) =>
    r.tem_arquivo && (
      <button
        type="button"
        className="icon-btn icon-btn-ok"
        title={`Baixar ${rotulo}: ${r.arquivo_nome ?? ""}`}
        disabled={ocupado !== null}
        onClick={() => baixar(r)}
      >
        <IconDownload size={17} />
      </button>
    );

  return (
    <section className="doc-fluxo">
      <h4 className="doc-fluxo-titulo">Arquivo e aprovação</h4>

      {obsoleto && !andamento ? (
        <p className="doc-espera">
          Documento <strong>obsoleto</strong> — não vale mais. As versões continuam guardadas no histórico; para
          voltar a usar, anexe uma nova revisão e passe pela aprovação.
        </p>
      ) : (
        <ol className="doc-passos" aria-label="Etapas da aprovação">
          {PASSOS.map((p, i) => (
            <li key={p} className={i < passo ? "feito" : i === passo ? "atual" : undefined}>
              <span className="doc-passo-marca">
                {i < passo || passo === PASSO_VIGENTE ? <IconCheck size={12} /> : i + 1}
              </span>
              <span className="doc-passo-nome">{p}</span>
            </li>
          ))}
        </ol>
      )}

      {carregando && !fluxo && <p className="doc-espera">Carregando…</p>}

      {vigente && (
        <div className="doc-versao">
          <div className="doc-versao-texto">
            <span className="doc-rotulo">Versão vigente</span>
            <span>
              <strong>{vigente.versao || "—"}</strong>
              {vigente.arquivo_nome ? ` · ${vigente.arquivo_nome}` : ""}
            </span>
            {assinaturas(vigente) && <small>{assinaturas(vigente)}</small>}
            {linhaValidade}
          </div>
          {botaoBaixar(vigente, "a versão vigente")}
        </div>
      )}
      {/* Vigente de antes do fluxo, sem arquivo no histórico: a revisão periódica aparece sozinha. */}
      {!vigente && linhaValidade && <div className="doc-versao">{linhaValidade}</div>}

      {andamento && (
        <div className="doc-versao">
          <div className="doc-versao-texto">
            <span className="doc-rotulo">
              {andamento.etapa === "Rascunho" ? "Rascunho (ainda não enviado)" : andamento.etapa}
            </span>
            <span>
              <strong>{andamento.versao || "—"}</strong>
              {andamento.arquivo_nome ? ` · ${andamento.arquivo_nome}` : ""}
            </span>
            {andamento.descricao && <small>O que mudou: {andamento.descricao}</small>}
            {andamento.enviado_em && andamento.etapa !== "Rascunho" && (
              <small>
                {elaborouERevisou(andamento) ? "Elaborado, revisado e enviado" : "Enviado"} por{" "}
                {andamento.elaborado_nome || "—"} em {dataHora(andamento.enviado_em)}
                {andamento.revisado_nome &&
                  !elaborouERevisou(andamento) &&
                  ` · revisado por ${andamento.revisado_nome}${andamento.revisado_substituto ? " (no lugar de quem revisa)" : ""}`}
                {andamento.aprovado_nome &&
                  ` · aprovado por ${andamento.aprovado_nome}${andamento.aprovado_substituto ? " (no lugar de quem aprova)" : ""}`}
              </small>
            )}
          </div>
          {botaoBaixar(andamento, "esta revisão")}
        </div>
      )}

      {devolucao && (
        <div className="doc-nota">
          <strong>
            Devolvido por {devolucao.usuario_nome || "—"} em {dataHora(devolucao.created_at)}
          </strong>
          {devolucao.observacao && <p>{devolucao.observacao}</p>}
          <small>Corrija o arquivo e envie de novo.</small>
        </div>
      )}

      {andamento?.etapa === "Aguardando aprovação" && !pode?.aprovar && (
        <p className="doc-espera">Elaborado e revisado. Aguardando a aprovação {quemAprova}.</p>
      )}
      {andamento?.etapa === "Aguardando publicação" && !pode?.publicar && (
        <p className="doc-espera">Aprovado. Aguardando a publicação {quemPublica}.</p>
      )}

      {/* Decisão de quem aprova ou publica — vale na hora. */}
      {fluxo && pode && decisao && (
        <div className="doc-decisao">
          <strong>
            {decisao === "publicar"
              ? pode.publicarNoLugar
                ? "Publicação no lugar de quem publica"
                : "Publicação"
              : pode.aprovarNoLugar
                ? "Aprovação no lugar de quem aprova"
                : "Sua aprovação"}
          </strong>
          {decisao === "aprovar" && andamento?.elaborado_nome && (
            <p className="doc-ajuda">
              {elaborouERevisou(andamento) ? "Elaborado e revisado" : "Enviado"} por {andamento.elaborado_nome}. Ao
              aprovar, o documento vai para a publicação {quemPublica}.
            </p>
          )}
          {decisao === "publicar" && (
            <p className="doc-ajuda">
              Aprovado {andamento?.aprovado_nome ? `por ${andamento.aprovado_nome}` : ""}. Ao publicar, esta revisão
              passa a ser a versão vigente{vigente ? " e a atual fica obsoleta (guardada no histórico)" : ""}.
            </p>
          )}
          {noLugar && (
            <p className="doc-ajuda">
              Quem {decisao === "publicar" ? "publica" : "aprova"} este documento é{" "}
              {nomes(decisao === "publicar" ? fluxo.publicadores : fluxo.aprovadores)}. Como administrador, você pode
              decidir no lugar — escreva o motivo no parecer; fica registrado no histórico.
            </p>
          )}
          <label className="field">
            <span>
              Parecer
              {noLugar && <em className="req"> *</em>}
            </span>
            <textarea
              rows={3}
              value={parecer}
              maxLength={2000}
              placeholder={
                noLugar
                  ? "Por que você está decidindo no lugar de quem foi escolhido (e, se devolver, o que corrigir)."
                  : `Obrigatório para devolver: diga o que corrigir. Para ${decisao}, é opcional.`
              }
              onChange={(e) => setParecer(e.target.value)}
            />
          </label>
          <div className="doc-botoes">
            <button
              type="button"
              className="btn-secondary"
              data-grava
              disabled={ocupado !== null}
              onClick={() => agir("devolver", parecer)}
            >
              {ocupado === "devolver" ? "Devolvendo…" : "Devolver para correção"}
            </button>
            <button
              type="button"
              className="btn-primary"
              data-grava
              disabled={ocupado !== null}
              onClick={() => agir(decisao, parecer)}
            >
              {decisao === "publicar"
                ? ocupado === "publicar"
                  ? "Publicando…"
                  : "Publicar — vira a versão vigente"
                : ocupado === "aprovar"
                  ? "Aprovando…"
                  : "Aprovar — mandar para publicação"}
            </button>
          </div>
        </div>
      )}

      {/* O campo do arquivo: sobe junto com o Salvar do pop-up. */}
      {podeAnexar && (
        <div className="doc-anexo">
          <span className="doc-rotulo">
            {andamento?.tem_arquivo
              ? "Trocar o arquivo desta revisão"
              : vigente
                ? "Arquivo da nova revisão"
                : "Arquivo do documento"}
          </span>
          <label
            className={`doc-arquivo${arrastando ? " arrastando" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setArrastando(true);
            }}
            onDragLeave={() => setArrastando(false)}
            onDrop={(e) => {
              e.preventDefault();
              setArrastando(false);
              escolher(e.dataTransfer.files?.[0]);
            }}
          >
            <input
              type="file"
              accept={ACEITOS.join(",")}
              onChange={(e) => {
                escolher(e.target.files?.[0]);
                e.target.value = ""; // permite escolher o mesmo arquivo de novo
              }}
            />
            <IconUpload size={20} />
            {anexo.file ? (
              <span>
                <strong>{anexo.file.name}</strong> · {tamanho(anexo.file.size)}
              </span>
            ) : (
              <span>Clique para escolher o arquivo ou arraste aqui</span>
            )}
            <small>PDF, Word, Excel, imagem, TXT ou ZIP · até {MAX_MB} MB</small>
          </label>
          {anexo.file && (
            <button type="button" className="btn-link" onClick={() => onAnexo({ ...anexo, file: null })}>
              Tirar este arquivo
            </button>
          )}

          {(anexo.file || andamento) && (
            <div className="doc-anexo-campos">
              <label className="field">
                <span>Revisão</span>
                <input
                  type="text"
                  value={anexo.versao}
                  maxLength={40}
                  onChange={(e) => onAnexo({ ...anexo, versao: e.target.value, alterado: true })}
                />
              </label>
              <label className="field">
                <span>O que mudou nesta revisão</span>
                <textarea
                  rows={2}
                  value={anexo.descricao}
                  maxLength={2000}
                  placeholder={vigente ? "Ex.: incluído o passo de bloqueio e etiquetagem" : "Ex.: emissão inicial"}
                  onChange={(e) => onAnexo({ ...anexo, descricao: e.target.value, alterado: true })}
                />
              </label>
            </div>
          )}

          <p className="doc-ajuda">
            O arquivo entra como rascunho. A elaboração e a revisão são suas: em{" "}
            <strong>Salvar e enviar para aprovação</strong>, ele vai para a aprovação de quem estiver em{" "}
            <strong>Quem aprova</strong> e depois para a publicação {quemPublica}; só então vira a versão vigente
            {vigente ? " — até lá, continua valendo a atual" : ""}.
          </p>
        </div>
      )}

      {(pode?.descartar || pode?.tornarObsoleto) && !obsoletando && (
        <div className="doc-links">
          {pode?.descartar && (
            <button
              type="button"
              className="btn-link"
              data-grava
              disabled={ocupado !== null}
              onClick={() =>
                agir("descartar", "", "Descartar este rascunho? O arquivo anexado a ele será apagado.")
              }
            >
              Descartar o rascunho
            </button>
          )}
          {pode?.tornarObsoleto && (
            <button
              type="button"
              className="btn-link"
              data-grava
              disabled={ocupado !== null}
              onClick={() => setObsoletando(true)}
            >
              Tornar o documento obsoleto
            </button>
          )}
        </div>
      )}

      {obsoletando && (
        <div className="doc-decisao">
          <strong>Tornar o documento obsoleto</strong>
          <p className="doc-ajuda">
            Ele deixa de valer, mas as versões continuam guardadas no histórico.
            {andamento ? " A revisão em andamento será descartada." : ""}
          </p>
          <label className="field">
            <span>
              Motivo <em className="req">*</em>
            </span>
            <textarea
              rows={2}
              value={motivo}
              maxLength={2000}
              placeholder="Ex.: substituído pelo PR-012; processo extinto"
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          <div className="doc-botoes">
            <button type="button" className="btn-secondary" onClick={() => setObsoletando(false)}>
              Voltar
            </button>
            <button
              type="button"
              className="btn-primary"
              data-grava
              disabled={ocupado !== null}
              onClick={() => agir("obsoleto", motivo)}
            >
              {ocupado === "obsoleto" ? "Registrando…" : "Confirmar: tornar obsoleto"}
            </button>
          </div>
        </div>
      )}

      {erro && <div className="form-error">{erro}</div>}
      {aviso && <div className="doc-aviso">{aviso}</div>}

      {fluxo && fluxo.revisoes.length > 0 && (
        <details className="doc-historico">
          <summary>Versões ({fluxo.revisoes.length})</summary>
          <ul>
            {fluxo.revisoes.map((r) => (
              <li key={r.id}>
                <div className="doc-versao-texto">
                  <span>
                    <strong>{r.versao || "—"}</strong> · {r.etapa}
                    {r.arquivo_nome ? ` · ${r.arquivo_nome}` : ""}
                  </span>
                  {assinaturas(r) && <small>{assinaturas(r)}</small>}
                  {r.descricao && r.descricao !== "Anexado antes do fluxo de aprovação" && (
                    <small>O que mudou: {r.descricao}</small>
                  )}
                </div>
                {botaoBaixar(r, "esta versão")}
              </li>
            ))}
          </ul>
        </details>
      )}

      {fluxo && fluxo.eventos.length > 0 && (
        <details className="doc-historico">
          <summary>Histórico ({fluxo.eventos.length})</summary>
          <ul>
            {fluxo.eventos.map((e) => (
              <li key={e.id}>
                <div className="doc-versao-texto">
                  <span>
                    <strong>{e.usuario_nome || "—"}</strong> {textoDoEvento(e)}
                    {e.versao ? ` (${e.versao})` : ""}
                  </span>
                  <small>{dataHora(e.created_at)}</small>
                  {e.observacao && e.acao !== "anexou" && <p className="doc-parecer">{e.observacao}</p>}
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
